/**
 * Rename, Duplicate and Archive for one row of the models list.
 *
 * These exist on the classic page as a per-row dropdown and were lost when the
 * new list replaced it, which made the flag a functional downgrade rather than
 * a swap: someone who wanted to rename a draft had no way to do it except open
 * the assistant and confirm a preview. The assistant doing the same four things
 * was meant to be an addition, not a replacement.
 *
 * Restored from `TenantPage` rather than rewritten, including the parts that
 * look incidental: the architect gate around the whole menu, the live-model
 * guards on Rename and Archive with the tooltips that say WHY, and
 * `DuplicationModelModal`, which is already a shared component.
 *
 * Deliberately NOT restored: "Deactivate primary fit". It is superKudu-only and
 * needs `hasDuplicateLiveStandardFits`, a fact about the whole list rather than
 * about one row, so it belongs to whatever renders the list.
 */
import { inject, observer } from 'mobx-react';
import React from 'react';
import {
  Button,
  Dropdown,
  Form,
  FormControl,
  FormGroup,
  FormLabel,
  Modal,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';

import ModelItem from '../../../models/ModelItem';
import { PROFILES_PERMISSIONS } from '../../../permissions';
import Store from '../../../store';
import checkPermissions from '../CheckPermissions';
import CustomToggle from '../generic_components/CustomToggle';
import DuplicationModelModal from '../modals/DuplicationModelModal';

interface ModelRowActionsProps {
  model: ModelItem;
  store?: Store;
}

type Open = 'none' | 'rename' | 'archive' | 'duplicate';

export const ModelRowActions = inject('store')(
  observer(
    class ModelRowActions extends React.Component<
      ModelRowActionsProps,
      { open: Open; name: string; busy: boolean }
    > {
      constructor(props: ModelRowActionsProps) {
        super(props);
        this.state = { open: 'none', name: props.model.name, busy: false };
      }

      close = () => this.setState({ open: 'none', busy: false });

      rename = async () => {
        const { model, store } = this.props;
        this.setState({ busy: true });
        await store?.updateModelName(model.modelId, this.state.name.trim());
        // The list is rendered from the store's model array, which this does not
        // update, and the classic page reloaded for the same reason.
        window.location.reload();
      };

      archive = async () => {
        const { model, store } = this.props;
        this.setState({ busy: true });
        await store?.updateModelArchiveStatus(
          model.modelId,
          !model.is_archived
        );
      };

      render() {
        const { model } = this.props;
        const { open, name, busy } = this.state;
        const live = !!model.live;
        // Same rule as the classic page: an empty name is not a rename, and
        // renaming to what it already is does nothing worth a request.
        const canRename = !!name.trim() && name.trim() !== model.name;

        const menu = (
          <td className="mh-actions">
            <Dropdown align="end">
              {/* The classic page's toggle, reused rather than re-drawn: it is
                  the same three-dot affordance people already look for, and it
                  carries the FontAwesome glyph this app ships. */}
              <Dropdown.Toggle
                as={CustomToggle}
                aria-label={`Actions for ${model.name}`}
              />
              <Dropdown.Menu>
                <OverlayTrigger
                  placement="left"
                  overlay={
                    <Tooltip id={`rename-${model.modelId}`}>
                      Live models cannot be renamed
                    </Tooltip>
                  }
                  trigger={live ? ['hover', 'focus'] : []}
                >
                  {/* `pointerEvents` kept: a disabled item swallows the hover
                      that shows the tooltip explaining why it is disabled. */}
                  <span style={{ pointerEvents: 'auto' }}>
                    <Dropdown.Item
                      disabled={live}
                      onClick={() =>
                        this.setState({ open: 'rename', name: model.name })
                      }
                    >
                      Rename
                    </Dropdown.Item>
                  </span>
                </OverlayTrigger>

                <Dropdown.Item
                  onClick={() => this.setState({ open: 'duplicate' })}
                >
                  Duplicate
                </Dropdown.Item>

                <Dropdown.Divider />

                <OverlayTrigger
                  placement="left"
                  overlay={
                    <Tooltip id={`archive-${model.modelId}`}>
                      Live models cannot be archived
                    </Tooltip>
                  }
                  trigger={live ? ['hover', 'focus'] : []}
                >
                  <span style={{ pointerEvents: 'auto' }}>
                    <Dropdown.Item
                      disabled={live}
                      onClick={() => this.setState({ open: 'archive' })}
                    >
                      {model.is_archived ? 'Un-archive' : 'Archive'}
                    </Dropdown.Item>
                  </span>
                </OverlayTrigger>
              </Dropdown.Menu>
            </Dropdown>

            <Modal show={open === 'rename'} onHide={this.close}>
              <Modal.Header closeButton>
                <Modal.Title>Rename model</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form
                  onSubmit={(event: React.FormEvent) => {
                    event.preventDefault();
                    if (canRename && !busy) void this.rename();
                  }}
                >
                  <FormGroup>
                    <FormLabel className="fw-bold">Model name</FormLabel>
                    <FormControl
                      autoFocus
                      type="text"
                      value={name}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        this.setState({ name: event.target.value })
                      }
                    />
                  </FormGroup>
                </Form>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="outline-primary" onClick={this.close}>
                  Cancel
                </Button>
                <Button disabled={!canRename || busy} onClick={this.rename}>
                  Rename
                </Button>
              </Modal.Footer>
            </Modal>

            <Modal show={open === 'archive'} onHide={this.close}>
              <Modal.Header closeButton>
                <Modal.Title>
                  {model.is_archived ? 'Un-archive' : 'Archive'} model
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                Are you sure you want to{' '}
                {model.is_archived ? 'un-archive' : 'archive'} this model?
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={this.close}>
                  Close
                </Button>
                <Button disabled={busy} onClick={this.archive}>
                  {model.is_archived ? 'Un-archive' : 'Archive'}
                </Button>
              </Modal.Footer>
            </Modal>

            <DuplicationModelModal
              fromModel={model.modelId}
              close={this.close}
              isShown={open === 'duplicate'}
            />
          </td>
        );

        return checkPermissions(PROFILES_PERMISSIONS.ARCHITECT, menu);
      }
    }
  )
);

export default ModelRowActions;
